import { Injectable, OnDestroy } from "@angular/core";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { CubiqWorker } from "shared/cubiq-services/worker/CubiqWorker";
import { PromiseSet, PrimaryPromiseSet, Roles } from "shared/auth-services";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { interval, Subject, Subscription } from "rxjs";
import { SystemConfigPrimaryService } from "shared/auth-services";
import { SysFeatures } from "shared/cubiq-services/model/core/SysFeatures";
import { NotificationService } from "../../notification/impl/NotificationService";
import { BFXNotificationType } from "shared/cubiq-services/model/core/notifications/BFXNotificationType";
import { BFXNotificationEventType } from "shared/cubiq-services/model/core/notifications/BFXNotificationEventType";
import { INotificationEvent } from "shared/cubiq-services/model/core/notifications/INotificationEvent";
import { BFXNotificationSubType } from "shared/cubiq-services/model/core/notifications/BFXNotificationSubType";
import {
  SYSMON_EVENT_FQDN,
  SYSMON_EVENT_ID,
  SYSMON_EVENT_SOURCE,
  SYSMON_EVENT_TIMESTAMP,
  SYSMON_EVENT_STATUS_DISP_TXT,
} from "shared/cubiq-services/lookup/model/impl/SystemMonitorLookup.constants";
import { NullProtoObject } from "../../../../helper-services/null-proto-object";
("shared/cubiq-services/lookup/model/impl/SystemMonitorLookup.constants");

export const SYSMON_CHANNEL: string = "HTML";
const DEFAULT_CLEANUP_HOURS: number = 24;

@Injectable()
export class SystemMonitorLookupService implements OnDestroy {
  public static readonly serverDateToken: string = "YYYY/MM/DD HH:mm:ss.SSS";

  private static readonly CLEANUP_INTERVAL: number = 15 * 60 * 1000;

  private events: CubiqTS.SystemMonitorEventImpl[] = [];

  private eventMap: NullProtoObject = Object.create(null);

  private statusMap: NullProtoObject = Object.create(null);

  private primingBuffer: CubiqTS.SystemMonitorEventImpl[] = [];

  protected primed: PromiseSet<boolean> = new PrimaryPromiseSet<boolean>();

  protected eventSubject: Subject<CubiqTS.SystemMonitorEventImpl> =
    new Subject<CubiqTS.SystemMonitorEventImpl>();

  protected clearedSubject: Subject<CubiqTS.SystemMonitorEventImpl> =
    new Subject<CubiqTS.SystemMonitorEventImpl>();

  private cleanupTaskSubscription: Subscription = null;

  constructor(
    private worker: CubiqWorker,
    private systemConfigService: SystemConfigPrimaryService,
    private notificationService: NotificationService,
  ) {
    worker.isAuth(`${this.constructor.name}`).then(
      () => {
        let lookupRequest: CubiqTS.TelemetrySubscriptionCatalogueRequestImpl =
          new CubiqTS.TelemetrySubscriptionCatalogueRequestImpl();
        lookupRequest.consumerChannel = SYSMON_CHANNEL;

        systemConfigService
          .hasAnyRoleOf([Roles.ROLE_VIEW_CONNECTION_STATUS, Roles.ROLE_VIEW_BENCHMARK_SUMMARY_RISK])
          .then((needsSysMonNotifs) => {
            if (true == needsSysMonNotifs) {
              this.cleanupTaskSubscription = interval(
                SystemMonitorLookupService.CLEANUP_INTERVAL,
              ).subscribe(this.cleanupEvents.bind(this));

              this.worker
                .getEncodableObservable(CubiqTS.SystemMonitorEventImpl.name)
                .subscribe((event: CubiqTS.SystemMonitorEventImpl) => {
                  try {
                    if (this.primingBuffer != null) {
                      this.primingBuffer.push(event);
                    } else {
                      this.logConnectionEvent(event);
                      this.processEvent(event);
                      this.eventSubject.next(event);
                    }
                  } catch (e) {
                    logger.out.error(
                      "SystemMonitorLookupService: Failed to process update event " + e,
                    );
                  }
                });

              this.worker
                .postServiceRequest(lookupRequest)
                .then((catalogue: CubiqTS.TelemetrySubscriptionCatalogue) => {
                  let primeCatalogue: Promise<void> = this.primeCatalogue(catalogue);
                  let primeHistoricalEvents: Promise<void> = this.primeHistorical();

                  Promise.all([primeCatalogue, primeHistoricalEvents])
                    .catch((error) => {
                      logger.out.error(
                        "SystemMonitorLookupService: Failed to process catalogue for channel: " +
                          SYSMON_CHANNEL +
                          ": " +
                          error,
                      );
                    })
                    .finally(() => {
                      this.primed.resolve(true);
                    });
                })
                .catch((error: Error) => {
                  logger.out.debug(
                    "SystemMonitorLookupService: SysMon subscription catalogue prime failed",
                  );
                  this.primed.reject(error);
                });
            } else {
              this.primed.resolve(true);
            }
          });
      },
      (reason) => {
        logger.out.debug("SystemMonitorLookupService: Failed to prime: " + reason);
        this.primed.resolve(false);
      },
    );
  }

  ngOnDestroy(): void {
    if (this.cleanupTaskSubscription != null) {
      this.cleanupTaskSubscription.unsubscribe();
      this.cleanupTaskSubscription = null;
    }
  }

  protected primeHistorical(): Promise<void> {
    let request: CubiqTS.SystemMonitorHistoryRequestImpl =
      new CubiqTS.SystemMonitorHistoryRequestImpl();

    return new Promise<void>((resolve, reject) => {
      this.worker
        .postServiceRequest(request)
        .then((report: CubiqTS.StandardReportImpl) => {
          if (
            report instanceof CubiqTS.StandardReportImpl &&
            Array.isArray(report.results) &&
            report.results.length > 0
          ) {
            let events: CubiqTS.SystemMonitorEventImpl[] = report.results;
            events.forEach((event: CubiqTS.SystemMonitorEventImpl) => this.events.push(event));
          }
          resolve();
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  protected primeCatalogue(catalogue: CubiqTS.TelemetrySubscriptionCatalogue): Promise<void> {
    let fQDNs: string[] = [];

    if (
      catalogue != null &&
      Array.isArray(catalogue.catalogueEntries) &&
      catalogue.catalogueEntries.length > 0
    ) {
      fQDNs = catalogue.catalogueEntries.map(
        (entry: CubiqTS.TelemetryCatalogueEntry) => entry.fQDN,
      );
    } else {
      logger.out.warn(
        "SystemMonitorLookupService: No subscription catalogue entries found for channel: " +
          SYSMON_CHANNEL,
      );
    }

    return new Promise<void>((resolve, reject) => {
      if (fQDNs.length > 0) {
        Promise.all(fQDNs.map(this.subscribeAndProcess.bind(this)))
          .then(() => {
            if (this.primingBuffer != null && this.primingBuffer.length > 0) {
              this.primingBuffer.forEach(this.processEvent.bind(this));
            }
            this.primingBuffer = null;
            resolve();
          })
          .catch((error) => {
            reject(error);
          });
      } else {
        resolve();
      }
    });
  }

  private logConnectionEvent(systemMonitorEvent: CubiqTS.SystemMonitorEventImpl): void {
    const fqdn = systemMonitorEvent.dataMap.get(SYSMON_EVENT_FQDN);
    const node: string = systemMonitorEvent.dataMap.get(SYSMON_EVENT_SOURCE);
    const status = systemMonitorEvent.dataMap.get(SYSMON_EVENT_STATUS_DISP_TXT);
    logger.out.info(
      `** Received System Monitor Event: FQDN=${fqdn}, node=${node}, status=${status}`,
    );
  }

  protected processEvent(
    event: CubiqTS.SystemMonitorEvent,
    skipEventsPool: boolean = false,
  ): boolean {
    let processed: boolean = event != null;

    if (processed) {
      let fqdn: string = event.dataMap.get(SYSMON_EVENT_FQDN);
      let source: string = event.dataMap.get(SYSMON_EVENT_SOURCE);

      if (source === "??") {
        source = null;
      }

      let key: string = fqdn + (source != null ? "_" + source : "");

      try {
        let oldEvent: CubiqTS.SystemMonitorEventImpl = this.eventMap[key];
        if (oldEvent != null) {
          let timeStampOld: string = oldEvent.dataMap.get(SYSMON_EVENT_TIMESTAMP);
          let timeStampNew: string = event.dataMap.get(SYSMON_EVENT_TIMESTAMP);

          if (timeStampOld != null && timeStampNew != null) {
            let tsOld = CubiqTS.moment.utc(
              timeStampOld,
              SystemMonitorLookupService.serverDateToken,
            );
            let tsNew = CubiqTS.moment.utc(
              timeStampNew,
              SystemMonitorLookupService.serverDateToken,
            );
            if (tsOld.isAfter(tsNew)) {
              logger.out.warn(
                this.constructor.name +
                  ": System monitor event received with older timestamp, ignoring " +
                  event.dataMap.get(SYSMON_EVENT_ID),
              );
              processed = false;
            }
          }
        }
      } catch (e) {
        logger.out.error(
          this.constructor.name + ": Failed to compare system monitor event timestamps." + e,
        );
      }

      if (processed) {
        let notif: INotificationEvent = (
          this.notificationService as NotificationService
        ).systemMonitorEventToNotification(event);

        if (!skipEventsPool) {
          this.events.push(event);
          if (this.notificationService.shouldNotify(BFXNotificationType.CONNECTION_MONITOR)) {
            this.notificationService.fireNotificationEvent(
              notif,
              BFXNotificationEventType.SYSTEM_MONITOR_EVENT,
            );
          }
        }
        delete this.eventMap[fqdn];

        if (notif != null && typeof notif === "object") {
          this.statusMap[fqdn] = notif.notifSubType;
        } else {
          delete this.statusMap[fqdn];
        }
        this.eventMap[key] = event;
      }
    }

    return processed;
  }

  public getCurrentStatus(fqdn: string): BFXNotificationSubType {
    return this.statusMap[fqdn];
  }

  protected subscribeAndProcess(fQDN: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.subscribe(fQDN)
        .then((events: CubiqTS.SystemMonitorEventSet) => {
          if (events != null && Array.isArray(events.events)) {
            events.events.forEach((event: CubiqTS.SystemMonitorEvent) =>
              this.processEvent(event, true),
            );
          }
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  protected subscribe(fQDN: string): Promise<CubiqTS.SystemMonitorEventSet> {
    return new Promise<CubiqTS.SystemMonitorEventSet>((resolve, reject) => {
      let subscriptionRequest: CubiqTS.TelemetrySubscriptionRequest =
        new CubiqTS.TelemetrySubscriptionRequestImpl();
      subscriptionRequest.consumerChannel = SYSMON_CHANNEL;
      subscriptionRequest.fQDN = fQDN;

      logger.out.info("SystemMonitorLookupService: Subscribing for FQDN " + fQDN);
      this.worker
        .postServiceRequest(subscriptionRequest)
        .then((events: CubiqTS.SystemMonitorEventSet) => {
          logger.out.info("SystemMonitorLookupService: Subscribed for FQDN: " + fQDN);
          resolve(events);
        })
        .catch((error: Error) => {
          console.debug("SystemMonitorLookupService: subscription for FQDN " + fQDN + " failed");
          reject(error);
        });
    });
  }

  public isPrimed(): Promise<boolean> {
    return this.primed.get();
  }

  public getAll(): CubiqTS.SystemMonitorEventImpl[] {
    return this.events;
  }

  public getEvents(): CubiqTS.SystemMonitorEventImpl[] {
    return Object.values(this.eventMap);
  }

  public getEventSubject(): Subject<CubiqTS.SystemMonitorEventImpl> {
    return this.eventSubject;
  }

  public getClearedSubject(): Subject<CubiqTS.SystemMonitorEventImpl> {
    return this.clearedSubject;
  }

  protected cleanupEvents(): void {
    this.systemConfigService
      .getSystemConfig(SysFeatures.connectionMonitorClearHours)
      .then((value: string) => {
        let cleanupHours: number = DEFAULT_CLEANUP_HOURS;

        if (value != null) {
          try {
            cleanupHours = parseInt(value, 10);
          } catch (e) {
            logger.out.warn(
              this.constructor.name +
                ": Could not read value for " +
                SysFeatures.connectionMonitorClearHours +
                " from system config. Value: " +
                value,
            );
          }
        }

        logger.out.info(
          this.constructor.name +
            ": Running event cleanup task, removing events older than " +
            cleanupHours +
            " hours.",
        );

        let cutoffTime: CubiqTS.Moment = CubiqTS.moment(Date.now()).subtract(cleanupHours, "hours");

        this.events = this.events.filter((event: CubiqTS.SystemMonitorEventImpl) => {
          if (event.dataMap != null && event.dataMap.get(SYSMON_EVENT_TIMESTAMP) != null) {
            let timestampStr: string = event.dataMap.get(SYSMON_EVENT_TIMESTAMP);
            let timestamp: CubiqTS.Moment = CubiqTS.moment.utc(
              timestampStr,
              SystemMonitorLookupService.serverDateToken,
            );
            let retained: boolean =
              timestamp != null && timestamp.isAfter && timestamp.isAfter(cutoffTime);

            if (!retained) {
              this.clearedSubject.next(event);
            }

            return retained;
          }

          return true;
        });
      });
  }
}
